"use client";

import React from "react";
import { motion } from "framer-motion";
import { Building2, ShoppingCart, Briefcase, MapPin, UserCheck, Coins, LockOpen as Unlock, Zap, Layers, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const audiences = [{
  title: "SMEs & growing brands",
  icon: Building2,
  description: "I work with small and mid-sized businesses that have outgrown basic tactics and need a dedicated search strategy. Ongoing management ensures your technical foundation stays clean and organic traffic converts into consistent revenue.",
  badge: "Growth Focus"
}, {
  title: "Ecommerce stores",
  icon: ShoppingCart,
  description: "I help online retailers rank their category pages and products for high-intent queries. Continuous optimization helps you capture shoppers at every stage of the buying journey while protecting your margins from rising ad costs.",
  badge: "Direct Sales"
}, {
  title: "B2B & lead generation",
  icon: Briefcase,
  description: "I support B2B firms looking to reach qualified decision-makers through search. We focus on building authority around high-value commercial keywords that fill your sales pipeline with relevant enquiries.",
  badge: "Pipeline Quality"
}, {
  title: "Local service providers",
  icon: MapPin,
  description: "I assist regional and multi-location businesses that rely on local search visibility to generate phone calls and bookings. Consistent local signals and Google Business Profile management keep your firm top-of-mind nearby.",
  badge: "Local Dominance"
}];
const advantages = [{
  title: "Direct access to me",
  icon: UserCheck,
  description: "You speak and strategize directly with me on every call and review, ensuring dedicated attention and senior strategic oversight throughout your campaign."
}, {
  title: "Transparent, honest pricing",
  icon: Coins,
  description: "Every deliverable and hourly allocation is itemized in plain terms, giving you complete clarity on where your investment goes and exactly what is delivered."
}, {
  title: "No long-term lock-in contracts",
  icon: Unlock,
  description: "I believe in earning your trust month after month through tangible organic growth, not through rigid 12-month lock-in agreements."
}, {
  title: "Faster execution & decisions",
  icon: Zap,
  description: "Without bureaucratic sign-off tiers, strategy changes and technical fixes are deployed quickly to capture emerging search opportunities."
}, {
  title: "Direct, practical guidance",
  icon: Layers,
  description: "You receive clear, actionable recommendations, direct implementation support, and straightforward reporting focused on real commercial progress."
}];
const checklistItems = ["Hands-on technical implementation and audits", "Custom keyword research tailored to your industry", "Transparent monthly reporting on revenue and rankings", "Direct communication via email, phone, and scheduled calls"];
export default function TargetAdvantages() {
  return <section id="targetaudienceandadvantages" data-nav="light" className="bg-background py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* PART 1: WHO THIS IS FOR */}
        <div className="mb-20 lg:mb-28">
          <div className="max-w-3xl mb-12">
            <Badge variant="secondary" className="mb-4 bg-secondary text-secondary-foreground font-medium px-3 py-1 rounded-full border border-accent/20">
              Client Profiles
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold font-serif text-primary tracking-tight">
              Who this is for
            </h2>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              I collaborate with ambitious business owners and marketing leaders
              who recognize that sustainable search growth requires structured,
              continuous refinement rather than quick fixes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {audiences.map((item, index) => {
            const Icon = item.icon;
            return <div key={item.title} data-index={index}>
                  <motion.div initial={{
                opacity: 0,
                y: 16
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: index * 0.1
              }}>
                    <Card className="h-full border border-border bg-card/80 backdrop-blur-xs hover:border-primary/40 hover:shadow-md transition-[border-color,box-shadow] duration-200">
                      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2.5 rounded-lg bg-secondary text-primary">
                            <Icon className="w-5 h-5 text-primary" />
                          </div>
                          <CardTitle className="text-xl font-bold font-serif text-foreground">
                            {item.title}
                          </CardTitle>
                        </div>
                        <Badge variant="outline" className="bg-background text-muted-foreground text-xs border-border">
                          {item.badge}
                        </Badge>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="text-base text-muted-foreground leading-relaxed">
                          {item.description}
                        </CardDescription>
                      </CardContent>
                    </Card>
                  </motion.div>
                </div>;
          })}
          </div>
        </div>

        <Separator className="my-12 bg-border" />

        {/* PART 2: WHY WORK WITH AN INDEPENDENT FREELANCE SEO CONSULTANT */}
        <div className="pt-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Column: Context & Statement */}
            <div className="lg:col-span-5 lg:sticky lg:top-24">
              <Badge variant="secondary" className="mb-4 bg-secondary text-secondary-foreground font-medium px-3 py-1 rounded-full border border-accent/20">
                Direct Partnership
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold font-serif text-primary tracking-tight leading-tight">
                Why work with an independent freelance SEO consultant
              </h2>
              <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
                An independent consulting partnership provides dedicated strategic
                focus, agile technical execution, and complete accountability for
                your search performance month after month.
              </p>

              <div className="mt-8 p-6 rounded-xl bg-secondary/40 border border-secondary">
                <h3 className="font-semibold text-foreground text-base mb-3 font-serif">
                  What you get on every engagement:
                </h3>
                <ul className="space-y-3">
                  {checklistItems.map((check, i) => <li key={i} data-index={i} className="flex items-start gap-2.5 text-sm text-foreground">
                      <CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                      <span>{check}</span>
                    </li>)}
                </ul>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
                  <Link to="/ContactPage">
                    Book a Free SEO Consultation
                    <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-border">
                  <Link to="/AboutMePage">Read About Me</Link>
                </Button>
              </div>
            </div>

            {/* Right Column: Key Advantages */}
            <div className="lg:col-span-7 space-y-4">
              {advantages.map((adv, index) => {
              const Icon = adv.icon;
              return <div key={adv.title} data-index={index}>
                    <motion.div initial={{
                  opacity: 0,
                  y: 14
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.35,
                  delay: index * 0.08
                }}>
                      <div className="p-6 rounded-xl border border-border bg-card/60 hover:bg-card hover:border-primary/30 transition-[color,background-color,border-color,box-shadow] duration-200">
                        <div className="flex items-start gap-4">
                          <div className="p-3 rounded-lg bg-secondary text-primary shrink-0">
                            <Icon className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <h3 className="text-lg font-bold font-serif text-foreground">
                              {adv.title}
                            </h3>
                            <p className="mt-2 text-sm sm:text-base text-muted-foreground leading-relaxed">
                              {adv.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}